import { axios } from '../lib'
import Cookies from 'js-cookie'
import { store } from '../index'
import { logout } from '../store/actions'

/**
 * Token Manager - Handles automatic token refresh
 */

// Check if token is about to expire (within 5 minutes)
export const isTokenExpiringSoon = (expiresAt) => {
  if (!expiresAt) return true
  
  const now = Date.now()
  const expirationTime = new Date(expiresAt).getTime()
  const fiveMinutes = 5 * 60 * 1000 // 5 minutes in milliseconds
  
  return (expirationTime - now) < fiveMinutes
}

// Check if token is expired
export const isTokenExpired = (expiresAt) => {
  if (!expiresAt) return true
  
  const now = Date.now()
  const expirationTime = new Date(expiresAt).getTime()
  
  return now >= expirationTime
}

// Get auth data from storage
export const getAuthData = () => {
  try {
    const authDataStr = localStorage.getItem('authData')
    if (authDataStr) {
      return JSON.parse(authDataStr)
    }
  } catch (error) {
    console.error('Error reading auth data:', error)
  }
  return null
}

// Refresh the access token using refresh token
export const refreshAccessToken = async () => {
  try {
    console.log('🔄 Attempting to refresh access token...')
    
    const authData = getAuthData()
    
    if (!authData || !authData.refreshToken) {
      console.warn('⚠️ No refresh token available')
      return null
    }

    // Call refresh token endpoint
    const response = await axios.post('/auth/refresh-token', {
      refreshToken: authData.refreshToken
    })

    if (response && response.data) {
      console.log('✅ Token refreshed successfully')
      
      const newAuthData = {
        ...authData,
        token: response.data.token,
        expires: response.data.expires || authData.expires
      }

      // Update localStorage
      localStorage.setItem('authData', JSON.stringify(newAuthData))
      
      // Update cookies
      const cookieOptions = {
        expires: 7,
        path: '/',
        sameSite: 'lax'
      }
      Cookies.set('tokenData', JSON.stringify(newAuthData), cookieOptions)
      
      console.log('✅ New token stored')
      
      return newAuthData
    }
  } catch (error) {
    console.error('❌ Token refresh failed:', error)
    
    // If refresh fails with 401/403, the refresh token is invalid
    // Log the user out
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      console.log('🚪 Refresh token invalid - logging out')
      store.dispatch(logout())
    }
    
    return null
  }
}

// Validate and refresh token if needed
export const validateAndRefreshToken = async () => {
  const authData = getAuthData()
  
  if (!authData || !authData.token) {
    console.log('❌ No auth data found')
    return false
  }

  // Check if token is expired or expiring soon
  if (isTokenExpired(authData.expires)) {
    console.log('⏰ Token expired - attempting refresh')
    const newAuthData = await refreshAccessToken()
    return !!newAuthData
  } else if (isTokenExpiringSoon(authData.expires)) {
    console.log('⏰ Token expiring soon - attempting refresh')
    await refreshAccessToken()
    return true
  }

  console.log('✅ Token is valid')
  return true
}

// Setup automatic token refresh on page load and visibility change
export const setupTokenRefresh = () => {
  console.log('🔧 Setting up token refresh listeners')

  // Refresh token when page loads
  validateAndRefreshToken()

  // Refresh token when user comes back to the tab
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      console.log('👀 Page visible - checking token')
      validateAndRefreshToken()
    }
  })

  // Refresh token when window regains focus
  window.addEventListener('focus', () => {
    console.log('🔍 Window focused - checking token')
    validateAndRefreshToken()
  })

  // Periodically check token (every 2 minutes)
  setInterval(() => {
    console.log('⏲️ Periodic token check')
    validateAndRefreshToken()
  }, 2 * 60 * 1000) // 2 minutes
}
